/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class LiveWidgetSegmentImpl
 * @hideconstructor
 * @classdesc
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/LiveWidgetSegmentAPI'
], (APIFactory, LiveWidgetSegmentAPI) => {
	class LiveWidgetSegment {

		constructor(options) {
			this.dashboard = options.features['API'];
			this.visDefinitions = options.features['VisDefinitions'];
			this.logger = options.features['Logger'];

			this._api = APIFactory.createAPI(this, [LiveWidgetSegmentAPI]);
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [LiveWidgetSegmentAPI]);
			}
			return this.api;
		}

		track(event = {}) {
			const supportedTrackActions = ['fromRecommendation', 'Recommended', 'Other', 'colorPalette'];
			if (supportedTrackActions.indexOf(event.category) === -1) {
				return;
			}

			const segment = this.dashboard.getFeature('segment');
			if (!segment) {
				this.logger.error(`Invalid segment object`);
				return;
			}

			let widgetType = event.visualization  ? event.visualization.getDefinition().getType() : null;

			let payload, action;

			if(!widgetType && event.category === 'fromRecommendation') {
				// Dragged a slot to the canvas
				// event.recommendation holds the visualization id
				widgetType = this._findWidgetType(event.recommendation) || event.recommendation;
				action = 'created';
				payload = {
					type: 'Created Object',
					widgetSource: 'fromRecommendation',
					details: {
						widgetSource: 'fromRecommendation',
						'widgetType': widgetType
					}
				};
			} else if (event.category === 'fromRecommendation' || event.category === 'Recommended' || event.category === 'Other') {
				// Modified the type
				action = 'modified';
				const newWidgetType = this._findWidgetType(event.info.visId);
				payload = {
					type: 'Updated Object',
					details: {
						'propertyId': event.category,
						'property.newValue': newWidgetType,
						'property.oldValue': widgetType
					}
				};
			} else if (event.category === 'colorPalette') {
				// Modified the colorPalette property
				action = 'modified';
				payload = {
					type: 'Updated Object',
					details: {
						'propertyId': event.category,
						'property.newValue': event.info.newValue,
						'property.oldValue': event.info.previousValue
					}
				};
			}

			if (payload) {
				const result = {
					...payload,
					details: {
						...payload.details,
						'widgetType': widgetType,
						'assetType': this.dashboard.getApplicationName()
					},
					trackType: 'data_widget',
					'custom.widgetType': widgetType
				};

				if (event.content) {
					result.object = event.content.getId();
				}
				const assetId = this.dashboard.getDashboardInfo().boardId;
				if (assetId) {
					result.details.assetId = assetId;
				}
				segment.track(action, () => (result));
			}
		}

		_findWidgetType(visId) {
			// The visChangerFlyoutView passes the type or the id depending the type or id for JQGrid or Crosstabs
			const visDef = this.visDefinitions.getByType(visId) || this.visDefinitions.getById(visId) || null;
			return visDef && visDef.getType();
		}

	}
	return LiveWidgetSegment;
});
